import type { ReactNode } from 'react';
import React, { Children } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { COLORS } from 'Components/utils/color/colors';
import { themes } from './StripButton.themes';
type StripButtonProps = {
  children: ReactNode;
  className?: string;
  color?: Colors;
  colorBorder?: Colors;
  dataTestId?: string;
  height?: number;
  onClick?: () => unknown;
  orientation?: 'top' | 'bottom';
  theme?: keyof typeof themes;
};

const StripButtonSvg = ({
  className,
  color,
  colorBorder,
  height,
  hasBottomOrientation,
}: {
  className?: string;
  color: Colors;
  colorBorder: Colors;
  height: number;
  hasBottomOrientation: boolean;
}) => (
  <svg
    className={classnames('fill-current', `text-${color}`, className)}
    viewBox="0 0 15 30"
    height={height}
    style={{
      flexBasis: height / 2,
    }}
  >
    <g>
      <g transform={`translate(-61.000000, ${hasBottomOrientation ? -155.5 : -156.5})`}>
        <g transform="translate(66.000000, 166.000000)">
          <g>
            <g>
              <path d="M10,21H-5L2.8-3.6C3.9-6.8,6.8-9,10-9" />
            </g>
          </g>
        </g>
      </g>
    </g>
    <g>
      <g transform={`translate(-61.000000, ${hasBottomOrientation ? -155.5 : -156.5})`}>
        <g transform="translate(66.000000, 166.000000)">
          <g>
            <g>
              <path
                style={{
                  strokeWidth: '1px',
                }}
                className={`fill-transparent stroke-current text-${colorBorder}`}
                d="M-5,21L2.8-3.6C3.9-6.8,6.8-9,10-9"
              />
            </g>
          </g>
        </g>
      </g>
    </g>
  </svg>
);

export const StripButton = ({
  children,
  className,
  color = COLORS.WHITE,
  colorBorder = COLORS.LIGHT_GREY,
  dataTestId = 'strip-button',
  height = 30,
  onClick,
  orientation = 'top',
  theme = 'default',
}: StripButtonProps) => {
  if (!Children.count(children)) {
    return null;
  }

  const { thInner, thRoot } = themes({
    color,
    colorBorder,
    onClick,
    orientation,
  })[theme];
  const hasBottomOrientation = orientation === 'bottom';
  const svgProps = {
    color,
    colorBorder,
    height,
    hasBottomOrientation,
  };
  const TagName = onClick ? 'button' : 'div';
  const btnProps = onClick
    ? {
        onClick,
        type: 'button',
      }
    : null;
  return (
    <TagName className={classnames(thRoot, className)} data-testid={dataTestId} {...btnProps}>
      <StripButtonSvg
        className={classnames('transform', {
          '-scale-y-1': hasBottomOrientation,
        })}
        {...svgProps}
      />
      <span
        className={thInner}
        style={{
          height,
        }}
      >
        {children}
      </span>
      <StripButtonSvg
        className={classnames('-scale-x-1 transform', {
          '-scale-y-1': hasBottomOrientation,
        })}
        {...svgProps}
      />
    </TagName>
  );
};
